import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useWaMsg, waUrl } from "@/lib/waTemplates";
import { toast } from "sonner";
import { Cake, Download, Loader2, MessageCircle } from "lucide-react";

const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

export function BirthdaysPanel({ list }) {
  const { user } = useAuth();
  const waMsg = useWaMsg();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [busy, setBusy] = useState(false);
  const link = user?.shop?.birthday_link;
  const [staff, setStaff] = useState([]);
  useEffect(() => {
    if (user?.shop_id === "all") { setStaff([]); return; }
    api.get("/birthdays", { params: { month } }).then(({ data }) => setStaff((data.items || []).filter((x) => x.kind === "funcionario"))).catch(() => setStaff([]));
  }, [month, user?.shop_id]);
  const rows = useMemo(() => [...list
    .filter((c) => !c.is_deleted && c.birth_date && Number(c.birth_date.slice(5, 7)) === month), ...staff]
    .sort((a, b) => a.birth_date.slice(8, 10).localeCompare(b.birth_date.slice(8, 10))), [list, month, staff]);
  const exportXlsx = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/birthdays.xlsx", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `aniversariantes-${MONTHS[month - 1].toLowerCase()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const msgFor = (c) => {
    const first = (c.name || "").split(" ")[0];
    const t = waMsg("birthday", { nome: first, cliente: first });
    return link ? `${t}\n${link}` : t;
  };
  return (
    <section data-testid="birthdays-panel" className="rounded-2xl border border-card2-border bg-card2 p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Cake className="h-5 w-5 text-amber-brand" />
        <h2 className="font-display text-lg font-bold uppercase text-zinc-100 mr-auto">Aniversariantes do mês</h2>
        <select data-testid="birthdays-month" value={month} onChange={(e) => setMonth(Number(e.target.value))}
          className="h-9 rounded-md bg-zinc-900 border border-zinc-800 px-2 text-sm text-zinc-100">
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <Button size="sm" variant="outline" data-testid="birthdays-export" disabled={busy || user?.shop_id === "all"} onClick={exportXlsx} className="bg-zinc-900 border-zinc-800">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Exportar lista
        </Button>
      </div>
      {rows.length === 0 ? (
        <p data-testid="birthdays-empty" className="text-sm text-zinc-500">Nenhum aniversariante em {MONTHS[month - 1]}. A data de nascimento fica no cadastro do cliente (Dados completos).</p>
      ) : (
        <ul className="divide-y divide-zinc-800">
          {rows.map((c) => {
            const href = waUrl(c.phone, msgFor(c));
            return (
              <li key={`${c.kind || "cliente"}-${c.id}`} data-testid={`birthday-row-${c.id}`} className="flex items-center gap-3 py-2">
                <span className="w-10 text-center font-display text-xl font-black text-amber-brand tabular-nums">{c.birth_date.slice(8, 10)}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-zinc-100 truncate">{c.name}{c.kind === "funcionario" && <span data-testid={`birthday-employee-${c.id}`} className="ml-2 rounded-full border border-sky-600 bg-sky-900/40 px-2 text-[10px] font-bold uppercase text-sky-200">Funcionário</span>}</div>
                  <div className="text-xs text-zinc-500">{c.phone || "sem telefone"}</div>
                </div>
                {href && (
                  <a data-testid={`birthday-wa-${c.id}`} href={href} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-green-600 hover:bg-green-500 text-white text-xs font-bold">
                    <MessageCircle className="h-4 w-4" /> Parabéns
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
